import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { patchCompany } from '../../api/company/company';
import { INITIAL_TUTORIAL_STATE, normalizeTutorialState } from './constants/initialTutorialState';

const TutorialContext = createContext();

const buildTutorialState = (persisted, stepId, { complete = false, skip = false } = {}) => {
  const base = normalizeTutorialState(persisted || INITIAL_TUTORIAL_STATE);
  const completedSteps = new Set(base.completedSteps);
  const skippedSteps = new Set(base.skippedSteps);

  if (complete) {
    completedSteps.add(stepId);
  }

  if (skip) {
    skippedSteps.add(stepId);
  }

  return {
    ...base,
    completedSteps: Array.from(completedSteps),
    skippedSteps: Array.from(skippedSteps),
    currentStep: Math.max(base.currentStep, stepId + 1),
  };
};

export const TutorialProvider = ({ children }) => {
  const { companyData, updateCompanyData } = useAuth();
  const [tutorialState, setTutorialState] = useState(INITIAL_TUTORIAL_STATE);
  const [isLoaded, setIsLoaded] = useState(false);

  const persistTutorialState = useCallback(async (nextState) => {
    if (!companyData?.id) return;
    const company = await patchCompany({
      companyId: companyData.id,
      company: { settings: { tutorial: nextState } },
    });
    updateCompanyData?.({ ...companyData, ...company });
  }, [companyData, updateCompanyData]);

  const markStepComplete = useCallback(async (stepId) => {
    if (!companyData?.id) return;

    setTutorialState((prev) => {
      if (prev.completedSteps.includes(stepId)) {
        return prev;
      }
      return buildTutorialState(prev, stepId, { complete: true });
    });

    try {
      const mergedState = buildTutorialState(
        companyData?.settings?.tutorial || tutorialState,
        stepId,
        { complete: true }
      );
      await persistTutorialState(mergedState);
      setTutorialState(mergedState);
    } catch (error) {
      console.error('Error saving tutorial state:', error);
    }
  }, [companyData, persistTutorialState, tutorialState]);

  const skipStep = useCallback(async (stepId) => {
    if (!companyData?.id) return;

    setTutorialState((prev) => {
      if (prev.skippedSteps.includes(stepId)) {
        return prev;
      }
      return buildTutorialState(prev, stepId, { skip: true });
    });

    try {
      const mergedState = buildTutorialState(
        companyData?.settings?.tutorial || tutorialState,
        stepId,
        { skip: true }
      );
      await persistTutorialState(mergedState);
      setTutorialState(mergedState);
    } catch (error) {
      console.error('Error skipping tutorial step:', error);
    }
  }, [companyData, persistTutorialState, tutorialState]);

  const dismissTutorial = useCallback(async () => {
    if (!companyData?.id) return;
    const newState = { ...tutorialState, isDismissed: true };
    setTutorialState(newState);
    try {
      await persistTutorialState(newState);
    } catch (error) {
      console.error('Error saving tutorial state:', error);
    }
  }, [companyData?.id, persistTutorialState, tutorialState]);

  const restoreTutorial = useCallback(async () => {
    if (!companyData?.id) return;
    const newState = { ...tutorialState, isDismissed: false };
    setTutorialState(newState);
    try {
      await persistTutorialState(newState);
    } catch (error) {
      console.error('Error restoring tutorial:', error);
    }
  }, [companyData?.id, persistTutorialState, tutorialState]);

  const resetTutorial = useCallback(async () => {
    if (!companyData?.id) return;
    setTutorialState(INITIAL_TUTORIAL_STATE);
    try {
      await persistTutorialState(INITIAL_TUTORIAL_STATE);
    } catch (error) {
      console.error('Error resetting tutorial state:', error);
    }
  }, [companyData?.id, persistTutorialState]);

  useEffect(() => {
    if (!companyData?.id) {
      setTutorialState(INITIAL_TUTORIAL_STATE);
      setIsLoaded(false);
      return;
    }
    const savedState = companyData?.settings?.tutorial;
    setTutorialState(savedState ? normalizeTutorialState(savedState) : INITIAL_TUTORIAL_STATE);
    setIsLoaded(true);
  }, [companyData?.id, companyData?.settings?.tutorial]);

  return (
    <TutorialContext.Provider
      value={{
        ...tutorialState,
        isLoaded,
        markStepComplete,
        skipStep,
        dismissTutorial,
        restoreTutorial,
        resetTutorial,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
